import { signal } from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { KDateFormat, KDateIs, KDateParse } from '@kiwid-app/kiwid-date-typescript';
import { populateTranslateValidation as populateTranslateValidationLib } from '@kiwid-app/kiwid-ui-lib-angular';
import { TranslateService } from '@ngx-translate/core';
import { CountrySettingHelper } from 'app/core/helpers/country-setting.helper';
import { normalizeSpaces, Util } from 'app/core/helpers/util';
import { FuseTranslationLoaderService } from 'app/core/translate/translation-loader.service';
import { first } from 'rxjs';
import { locale as english } from './i18n/en';
import { locale as spanish } from './i18n/es';
import { locale as portuguese } from './i18n/pt';
import { luhnCheck } from 'app/core/helpers/luhn.helper';

type IBaseForm = {
    message?: string;
};

type IDateValidForm = IBaseForm & {
    format: string;
};

type IMaxDateForm = IBaseForm & {
    format: string;
    maxDate: Date;
    parseValue?: (value: string) => string;
};

type IMinDateForm = IBaseForm & {
    format: string;
    minDate: Date;
    parseValue?: (value: string) => string;
};

type IDisableMinuteForm = IBaseForm & {
    format: string;
};

type IMinLengthForm = IBaseForm & {
    minLength: number;
};

type IMaxLengthForm = IBaseForm & {
    maxLength: number;
};

type INumberForm = IBaseForm & {
    min?: number;
    max?: number;
    parse?: (value: number) => string;
    isOnlyInteger?: boolean;
};

type IIsDocumentForm = IBaseForm & {
    type: 'business' | 'personal' | 'both';
};

type IConditionalForm = {
    enabled: boolean;
    control: AbstractControl;
    message: string;
    key?: string;
};

type IMustMatchForm = {
    matchingControl: AbstractControl;
    message?: string;
};

type IMaskForm = IBaseForm & {
    mask: string;
    dropSpecialCharacters?: boolean;
};

type IMatchForm = {
    field: AbstractControl;
    message: string;
};

type IIsNotWeekendDateForm = IBaseForm & {
    format: string;
};

export const translateFormValidation = signal<any>({});

export const populateTranslateValidation = (fuseTranslationLoaderService: FuseTranslationLoaderService, translateService: TranslateService) => {
    fuseTranslationLoaderService.loadTranslations(portuguese, english, spanish);
    translateService
        .get('FORM_VALIDATION')
        .pipe(first())
        .subscribe((response: any) => {
            translateFormValidation.set(response);
            populateTranslateValidationLib(response);
        });
};

export function translateForm(
    key: string,
    params?: {
        [key: string]: string;
    },
    type?: 'one' | 'other' | 'zero',
) {
    return Util.translateKey(translateFormValidation(), key, params, type);
}

export function dateValidForm(data: IDateValidForm): ValidatorFn {
    const { format, message = translateForm('INVALID_DATE', { format: format.toUpperCase() }) } = data;

    return (control: AbstractControl): ValidationErrors | null => {
        const rawValue = control.getRawValue();

        if (!!rawValue && (rawValue.length !== format.length || !KDateIs({ value: rawValue, format }, 'valid'))) {
            return { message };
        }

        return null;
    };
}

export function maxDateForm(data: IMaxDateForm): ValidatorFn {
    const { format, maxDate, parseValue = (value) => value, message = translateForm('MAX_DATE', { max: KDateFormat(maxDate, format) }) } = data;

    return (control: AbstractControl): ValidationErrors | null => {
        const rawValue = parseValue(String(control.getRawValue() ?? ''));
        if (
            !!rawValue &&
            KDateIs(
                {
                    value: rawValue,
                    format,
                },
                'after',
                maxDate,
            )
        ) {
            return {
                message,
            };
        }
    };
}

export function disableMinuteForm(data: IDisableMinuteForm): ValidatorFn {
    const { format, message = translateForm('DISABLE_MINUTE') } = data;

    return (control): ValidationErrors | null => {
        const rawValue = control.getRawValue();

        if (!!rawValue && KDateIs({ value: rawValue, format }, 'valid')) {
            const date = KDateParse({ value: rawValue, format });

            if (date.getMinutes() !== 0) {
                return { message };
            }
        }
    };
}

export function minDateForm(data: IMinDateForm): ValidatorFn {
    const { format, minDate, parseValue = (value) => value, message = translateForm('MIN_DATE', { min: KDateFormat(minDate, format) }) } = data;

    return (control: AbstractControl): ValidationErrors | null => {
        const rawValue = parseValue(String(control.getRawValue() ?? ''));
        if (
            !!rawValue &&
            KDateIs(
                {
                    value: rawValue,
                    format,
                },
                'before',
                minDate,
            )
        ) {
            return {
                message,
            };
        }
    };
}

export function urlForm(data: IBaseForm): ValidatorFn {
    const { message = translateForm('URL') } = data;

    return (control: AbstractControl): ValidationErrors | null => {
        const rawValue = control.getRawValue();

        if (!!rawValue) {
            const urlRegex = /^(ftp|http|https):\/\/[^ "]+(\.[^ "]+)+$/;
            if (!urlRegex.test(rawValue)) {
                return { message };
            }
        }

        return null;
    };
}

export function emailForm(data: IBaseForm = {}): ValidatorFn {
    const { message = translateForm('EMAIL') } = data;

    return (control: AbstractControl): ValidationErrors | null => {
        const rawValue = (control.getRawValue() ?? '').trim().toLowerCase();

        if (!!rawValue) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(rawValue)) {
                return { message };
            }

            const patternRegex = '^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$';
            if (!RegExp(patternRegex).test(rawValue)) {
                return { message };
            }
        }

        return null;
    };
}

export function patternForm(pattern: string | RegExp, data: IBaseForm = {}): ValidatorFn {
    const { message = translateForm('REGEXP') } = data;

    return (control: AbstractControl): ValidationErrors | null => {
        const rawValue = control.getRawValue();

        if (!!rawValue) {
            if (!RegExp(pattern).test(rawValue)) {
                return { message };
            }
        }

        return null;
    };
}

export function requiredForm(data: IBaseForm = {}): ValidatorFn {
    const { message = translateForm('REQUIRED') } = data;

    return (control: AbstractControl): ValidationErrors | null => {
        const rawValue = String(control.getRawValue() ?? '').trim();

        if (rawValue === '') {
            return { message };
        }

        return null;
    };
}

export function fullNameForm(data: IBaseForm = {}): ValidatorFn {
    const { message } = data;

    return (control: AbstractControl): ValidationErrors | null => {
        const rawValue = String(control.getRawValue() ?? '').trim();

        if (rawValue === '') {
            return null;
        }

        const names = normalizeSpaces(rawValue).split(' ');

        if (names.length < 2) {
            return { message: message || translateForm('MINIMUM_TWO_NAMES') };
        }

        const firstName = names[0].toLowerCase();
        const secondName = names[1].toLowerCase();

        if (firstName === secondName) {
            return { message: message || translateForm('DUPLICATE_NAMES_NOT_ALLOWED') };
        }

        return null;
    };
}

export function requiredTrueForm(data: IBaseForm = {}): ValidatorFn {
    const { message = translateForm('REQUIRED') } = data;

    return (control: AbstractControl): ValidationErrors | null => {
        const rawValue = control.getRawValue() ?? false;

        if (!rawValue) {
            return { message };
        }

        return null;
    };
}

export function maskForm(data: IMaskForm): ValidatorFn {
    const { mask, dropSpecialCharacters = false, message = translateForm('MASK') } = data;

    return (control: AbstractControl): ValidationErrors | null => {
        const rawValue = control.getRawValue();

        const dropSpecialCharactersMask = (currentMask: string) => {
            return currentMask.replaceAll('.', '').replaceAll(',', '').replaceAll('/', '').replaceAll('-', '');
        };

        if (
            mask?.split('||')?.every((maskSome) => {
                const currentMask = dropSpecialCharacters ? dropSpecialCharactersMask(maskSome) : maskSome;
                return !!rawValue && currentMask.length !== String(rawValue).length;
            })
        ) {
            return { message };
        }

        return null;
    };
}

export function minLengthForm(data: IMinLengthForm): ValidatorFn {
    const { minLength, message = translateForm('MIN_LENGTH', { min: String(minLength) }) } = data;

    return (control: AbstractControl): ValidationErrors | null => {
        const rawValue = control.getRawValue();

        if (!!rawValue && String(rawValue).length < minLength) {
            return { message };
        }

        return null;
    };
}

export function maxLengthForm(data: IMaxLengthForm): ValidatorFn {
    const { maxLength, message = translateForm('MAX_LENGTH', { max: String(maxLength) }) } = data;

    return (control: AbstractControl): ValidationErrors | null => {
        const rawValue = control.getRawValue();

        if (!!rawValue && String(rawValue).length > maxLength) {
            return { message };
        }

        return null;
    };
}

export function numberForm(data: INumberForm = {}): ValidatorFn {
    const { max, min, isOnlyInteger = false, message, parse = (value) => String(value) } = data;

    return (control: AbstractControl): ValidationErrors | null => {
        if (!String(control.getRawValue() ?? '').trim()) {
            return;
        }

        const rawValue = Number(control.getRawValue());
        const suffixTranslate = isOnlyInteger ? '_INTEGER' : '';

        if (isNaN(rawValue)) {
            return { message: !!message ? message : translateForm(`NUMBER${suffixTranslate}`.trim()) };
        }

        if (isOnlyInteger && !Number.isInteger(rawValue)) {
            return { message: !!message ? message : translateForm('NUMBER_INTEGER') };
        }

        if (typeof min === 'number' && min > rawValue) {
            return { message: !!message ? message : translateForm('MIN_NUMBER', { min: parse(min) }) };
        }

        if (typeof max === 'number' && max < rawValue) {
            return { message: !!message ? message : translateForm('MAX_NUMBER', { max: parse(max) }) };
        }
    };
}

export function uuidForm(data: IBaseForm = {}): ValidatorFn {
    const { message = translateForm('UUID') } = data;
    return (control: AbstractControl): ValidationErrors | null => {
        const rawValue = control.getRawValue();

        if (!!rawValue && !RegExp(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i).test(control.value)) {
            return { message };
        }
    };
}

export function isDocumentForm(countrySettingHelper: CountrySettingHelper, data: IIsDocumentForm): ValidatorFn {
    const { message = translateForm('INVALID_DOCUMENT'), type = 'both' } = data;

    return (control: AbstractControl): ValidationErrors | null => {
        const rawValue = control.getRawValue();

        const checkCpf = () => {
            if (rawValue.length !== 11) {
                return { message };
            }

            if (
                rawValue === '00000000000' ||
                rawValue === '11111111111' ||
                rawValue === '22222222222' ||
                rawValue === '33333333333' ||
                rawValue === '44444444444' ||
                rawValue === '55555555555' ||
                rawValue === '66666666666' ||
                rawValue === '77777777777' ||
                rawValue === '88888888888' ||
                rawValue === '99999999999'
            ) {
                return { message };
            }

            let sum = 0;
            for (let i = 1; i <= 9; i++) {
                sum = sum + Number(rawValue.substring(i - 1, i)) * (11 - i);
            }
            let rest = (sum * 10) % 11;
            if (rest === 10 || rest === 11) {
                rest = 0;
            }
            if (rest !== Number(rawValue.substring(9, 10))) {
                return { message };
            }
            sum = 0;
            for (let i = 1; i <= 10; i++) {
                sum = sum + Number(rawValue.substring(i - 1, i)) * (12 - i);
            }
            rest = (sum * 10) % 11;
            if (rest === 10 || rest === 11) {
                rest = 0;
            }
            if (rest !== Number(rawValue.substring(10, 11))) {
                return { message };
            }

            return null;
        };

        const checkCnpj = () => {
            if (rawValue.length !== 14) {
                return { message };
            }

            if (
                rawValue === '00000000000000' ||
                rawValue === '11111111111111' ||
                rawValue === '22222222222222' ||
                rawValue === '33333333333333' ||
                rawValue === '44444444444444' ||
                rawValue === '55555555555555' ||
                rawValue === '66666666666666' ||
                rawValue === '77777777777777' ||
                rawValue === '88888888888888' ||
                rawValue === '99999999999999'
            ) {
                return { message };
            }

            let size = rawValue.length - 2;
            let numbers = rawValue.substring(0, size);
            const digits = rawValue.substring(size);
            let sum = 0;
            let pos = size - 7;
            for (let i = size; i >= 1; i--) {
                sum += numbers.charAt(size - i) * pos--;
                if (pos < 2) {
                    pos = 9;
                }
            }
            let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
            if (result !== Number(digits.charAt(0))) {
                return { message };
            }
            size = size + 1;
            numbers = rawValue.substring(0, size);
            sum = 0;
            pos = size - 7;
            for (let i = size; i >= 1; i--) {
                sum += numbers.charAt(size - i) * pos--;
                if (pos < 2) {
                    pos = 9;
                }
            }
            result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
            if (result !== Number(digits.charAt(1))) {
                return { message };
            }

            return null;
        };

        const checkCpfOrCnpj = () => {
            if (!rawValue) {
                return null;
            }

            if (rawValue.length === 11) {
                return checkCpf();
            }

            if (rawValue.length === 14) {
                return checkCnpj();
            }

            return { message };
        };

        if (!!rawValue && rawValue?.length > 0) {
            if (countrySettingHelper?.countrySetting()?.code === 'BRA') {
                if (type === 'personal') {
                    return checkCpf();
                }

                if (type === 'business') {
                    return checkCnpj();
                }

                return checkCpfOrCnpj();
            }
        }

        return null;
    };
}

export function letterForm(data: IBaseForm = {}): ValidatorFn {
    const { message = translateForm('LETTER') } = data;

    return (control: AbstractControl): ValidationErrors | null => {
        const rawValue = control.getRawValue();

        if (!!rawValue && !RegExp(/^[a-zà-úA-ZÀ-Ú ]*$/).test(rawValue)) {
            return { message };
        }
    };
}

export function letterAndNumberForm(data: IBaseForm = {}): ValidatorFn {
    const { message = translateForm('LETTER_AND_NUMBER') } = data;

    return (control: AbstractControl): ValidationErrors | null => {
        const rawValue = control.getRawValue();

        if (!!rawValue && !RegExp(/^[a-zà-úA-ZÀ-Ú0-9' ]*$/).test(rawValue)) {
            return { message };
        }
    };
}

export function conditionalForm(data: IConditionalForm) {
    const { enabled, control, message, key = 'conditional' } = data;
    const conditional = key;

    if (enabled) {
        control.setErrors({ message, conditional });
        control.markAllAsTouched();
        return;
    }

    if (control.errors?.conditional === key) {
        control.setErrors(null);
        control.markAllAsTouched();
    }
}

export function mustMatchForm(data: IMustMatchForm): ValidatorFn | null {
    const { matchingControl, message = translateForm('MUST_MATCH') } = data;

    return (control: AbstractControl): ValidationErrors | null => {
        if (control.errors && !matchingControl.errors) {
            return;
        }
        if (control.value !== matchingControl.value) {
            control.setErrors({ message });
            return { message };
        } else {
            control.setErrors(null);
            return;
        }
    };
}

export function requiredExistForm(controlNames: string[], data: IBaseForm = {}): ValidationErrors | null {
    const { message = translateForm('REQUIRED') } = data;

    return (formGroup: FormGroup) => {
        for (const controlName of controlNames) {
            const control = formGroup.controls[controlName];
            if (control.value) {
                controlNames.forEach((name) => {
                    const controlValidator = formGroup.controls[name];

                    if (!controlValidator.value) {
                        return { message };
                    }

                    return null;
                });
                return;
            }

            return null;
        }
    };
}

export function changeValidationForm(form: AbstractControl, validators: ValidatorFn[], emitEvent = false) {
    form.clearValidators();

    if (validators.length > 0) {
        form.addValidators(validators);
    }

    form.updateValueAndValidity({ emitEvent });
}

export function checkMatchForm(data: IMatchForm): ValidatorFn {
    const { field, message } = data;

    return (control: AbstractControl): ValidationErrors | null => {
        const rawValue = control.getRawValue();
        const fieldRawValue = field.getRawValue();
        if (rawValue?.length > 0 && rawValue !== fieldRawValue) {
            return { message };
        }
    };
}

export function strongPasswordForm(data: IBaseForm = {}): ValidatorFn {
    const { message = translateForm('STRONG_PASSWORD') } = data;

    return (control: AbstractControl): ValidationErrors | null => {
        const rawValue = control.getRawValue();

        if (!!rawValue && !RegExp(/((?=.*\d)|(?=.*\W+))(?![.\n])(?=.*[A-Z])(?=.*[a-z]).*$/).test(rawValue) && rawValue?.length > 0) {
            return { message };
        }
    };
}

export function isNotWeekendDateForm(data: IIsNotWeekendDateForm): ValidatorFn {
    const { format, message = translateForm('NOT_WEEKEND') } = data;

    return (control: AbstractControl): ValidationErrors | null => {
        const rawValue = control.getRawValue();
        if (
            !!rawValue &&
            KDateIs(
                {
                    value: rawValue,
                    format,
                },
                'weekend',
            )
        ) {
            return {
                message,
            };
        }
    };
}

export function luhnForm(data: IBaseForm = {}): ValidatorFn {
    const { message = translateForm('CARD_NUMBER_INVALID') } = data;

    return (control: AbstractControl): ValidationErrors | null => {
        const rawValue = control.getRawValue();
        const isValid = luhnCheck(control.value);

        return isValid ? null : { message };
    };
}
